import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type {
  CartridgeInfo,
  PublicCartridgeListResponse,
} from "@/types/cartridge";
import { useApiPaginated } from "@/hooks/useApiPaginated";
import { queryKeys } from "@/lib/queryKeys";

export function useCartridges() {
  const query = useQuery({
    queryKey: queryKeys.cartridges.installed(),
    queryFn: () => api<CartridgeInfo[]>("/api/v2/cartridges/installed"),
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return {
    cartridges: query.data ?? [],
    loading: query.isLoading,
    error: query.error ? "Failed to load cartridges" : "",
    refetch,
  };
}

// Paginated variant for the "Installed" tab table (S10400.14). useCartridges()
// above is kept unpaginated on purpose — it feeds the Catalog tab's
// installedNames cross-reference, which needs the full set, not one page.
export function useCartridgesPaginated(
  page: number,
  pageSize: number,
  q = "",
) {
  const { data, total, loading, error, refetch } =
    useApiPaginated<CartridgeInfo>(
      queryKeys.cartridges.installedPaginated({ page, pageSize, q }),
      "/api/v2/cartridges/installed",
      page,
      pageSize,
      { q },
      "Failed to load cartridges",
    );
  return { cartridges: data, total, loading, error, refetch };
}

export interface CatalogParams {
  keyword?: string;
  limit: number;
  offset: number;
}

const EMPTY_CATALOG: PublicCartridgeListResponse = {
  items: [],
  total_count: 0,
};

export function useCartridgeCatalog(params: CatalogParams) {
  const query = useQuery({
    queryKey: queryKeys.cartridges.catalog(params),
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set("limit", String(params.limit));
      qs.set("offset", String(params.offset));
      if (params.keyword) {
        qs.set("keyword", params.keyword);
      }
      return api<PublicCartridgeListResponse>(
        `/api/v2/cartridges/public?${qs.toString()}`,
      );
    },
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return {
    catalog: query.data ?? EMPTY_CATALOG,
    loading: query.isLoading,
    error: query.error ? "Failed to load catalog" : "",
    refetch,
  };
}
